<template>
  <component
    v-if="configLoaded"
    id="app"
    class="app"
    :class="[wrapperClass, defaultClass]"
    :is="layout"
  >
    <router-view :key="$route.fullPath" />
  </component>
  <div class="app-loader" v-else>
    <Loader :loading="true" :text="$t('Loading Application...')" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import axios from 'axios';
import { Loader } from '@/modules/core';

const DefaultLayout = () => import(/* webpackChunkName: "vsf-layout-default" */ './modules/core/layouts/Default');
const EmptyLayout = () => import(/* webpackChunkName: "vsf-layout-empty" */ './modules/core/layouts/Empty');
const SimpleLayout = () => import(/* webpackChunkName: "vsf-layout-simple" */ './modules/core/layouts/Simple');

export default {
  metaInfo() {
    return {
      title: this.storeConfig?.seo?.defaultTitle || 'BMW Level4',
      neta: [
        {
          name: 'description',
          content: this.storeConfig?.seo?.defaultMetaDescription || 'BMW Level4 - Store',
        },
        {
          name: 'keywords',
          content: this.storeConfig?.seo?.defaultMetaKeywords || 'BMW Level4 - Store',
        },
      ],
    };
  },
  created() {
    axios.interceptors.response.use(
      undefined,
      (err) => new Promise(() => {
        const { response } = err;
        if (response?.status === 401) {
          this.logout().then(() => {
            this.$router.push('/login');
          });
        }
        throw err;
      }),
    );

    axios
      .all([
        this.fetchConfig(),
        this.fetchCurrentStore(),
        this.fetchLanguages(),
        this.fetchWarehouses(),
        this.fetchCurrencies(),
        this.fetchCountries(),
        this.fetchStateProvince(),
        this.fetchPages(),
      ])
      .then(() => {
        if (this.isAuthenticated) {
          this.fetchSimpleCart();
          this.fetchShoppingList();
          this.fetchVehicleFilter({
            filterType: 'brand',
          });
        }
      })
      .finally(() => {
        this.configLoaded = true;
      });
  },
  data() {
    return {
      configLoaded: false,
    };
  },
  computed: {
    layout() {
      return `${this.$route.meta.layout || 'default'}-layout`;
    },
    defaultClass() {
      return this.$route.name ? this.$route.name.toLowerCase().replace(/\s/g, '-') : '';
    },
    wrapperClass() {
      return this.$route.meta.wrapperClass;
    },
    ...mapGetters({
      isAuthenticated: 'user/isAuthenticated',
      storeConfig: 'core/storeConfig',
    }),
  },
  components: {
    DefaultLayout,
    EmptyLayout,
    SimpleLayout,
    Loader,
  },
  methods: {
    ...mapActions({
      logout: 'user/logout',
      fetchSimpleCart: 'checkout/fetchSimpleCart',
      fetchWarehouses: 'core/fetchWarehouses',
      fetchCountries: 'core/fetchCountries',
      fetchConfig: 'core/fetchConfig',
      fetchLanguages: 'core/fetchLanguages',
      fetchStateProvince: 'core/fetchStateProvince',
      fetchCurrentStore: 'core/fetchCurrentStore',
      fetchCurrencies: 'core/fetchCurrencies',
      fetchVehicleFilter: 'level4/fetchVehicleFilter',
      fetchPages: 'page/fetchPages',
      fetchShoppingList: 'user/fetchShoppingList',
    }),
  },
};
</script>

<style scoped lang="scss">
.app-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}
</style>
